import { createContext, memo, type ReactNode, useContext, useMemo } from 'react';

import { reportErrorToBugsnag } from '@air/utils-error';

import { isDevOrTestStage } from '~/swr-hooks/utils';

export type MainSideNavItemHoverExpandContextValue = {
  expandOnItemHover: (itemId?: string) => void;
};

export type MainSideNavItemHoverExpandProviderProps = {
  children: ReactNode;
  expandOnItemHover: (itemId?: string) => void;
};

const defaultContextValue: MainSideNavItemHoverExpandContextValue = {
  expandOnItemHover: () => {},
};

const MainSideNavItemHoverExpandContext = createContext<MainSideNavItemHoverExpandContextValue>(defaultContextValue);

export const useMainSideNavItemHoverExpand = () => {
  const ctx = useContext(MainSideNavItemHoverExpandContext);

  if (ctx === defaultContextValue) {
    const error = new Error('useMainSideNavItemHoverExpand must be used within MainSideNavItemHoverExpandProvider');

    if (isDevOrTestStage()) {
      throw error;
    }

    reportErrorToBugsnag({
      error,
      context: 'Main side nav item hover expand context not found',
    });
  }

  return ctx;
};

export const MainSideNavItemHoverExpandProvider = memo(
  ({ children, expandOnItemHover }: MainSideNavItemHoverExpandProviderProps) => {
    const contextValue = useMemo(
      () => ({
        expandOnItemHover,
      }),
      [expandOnItemHover],
    );

    return (
      <MainSideNavItemHoverExpandContext.Provider value={contextValue}>
        {children}
      </MainSideNavItemHoverExpandContext.Provider>
    );
  },
);

MainSideNavItemHoverExpandProvider.displayName = 'MainSideNavItemHoverExpandProvider';
